import React, { useEffect, useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useCommon } from 'src/domains/common/Common';
import { SocketStatsBarState } from './SocketStatsBarState';
import {
    BAR_HEIGHT_PX,
    BarActions,
    BarButton,
    BarRoot,
    BarTitle,
    InfoButton,
    StatChip,
    formatMapStat,
    statLevel,
} from './SocketStatsBar.style';
import { SocketSubscriptionDebugSnapshot } from 'src_common/common/websocket2/socketSubscriptionDebugStats';
import { SocketStatsBarLegend } from './SocketStatsBarLegend';

const REFRESH_MS = 500;

const SocketStatsBarInner = observer('SocketStatsBarInner', () => {
    const common = useCommon();
    const barState = SocketStatsBarState.get(common);
    const [legendOpen, setLegendOpen] = useState(false);
    const [snapshot, setSnapshot] = useState<SocketSubscriptionDebugSnapshot>(() =>
        common.models.getSocketSubscriptionDebugSnapshot()
    );

    useEffect(() => {
        const timer = setInterval(() => {
            setSnapshot(common.models.getSocketSubscriptionDebugSnapshot());
        }, REFRESH_MS);

        return (): void => {
            clearInterval(timer);
        };
    }, [common]);

    useEffect(() => {
        document.documentElement.style.setProperty('--socket-stats-bar-height', `${BAR_HEIGHT_PX}px`);
        document.body.style.paddingTop = `${BAR_HEIGHT_PX}px`;

        return (): void => {
            document.documentElement.style.removeProperty('--socket-stats-bar-height');
            document.body.style.paddingTop = '';
        };
    }, []);

    const { connection, maps, wireActiveSubscriptions, wireCacheEntries } = snapshot;
    const connLabel = connection.isConnected ? 'open' : 'closed';

    return (
        <>
            <BarRoot>
                <BarTitle>WS stats</BarTitle>
                <StatChip level={connection.isConnected ? 'ok' : 'warn'}>
                    Events socket: {connLabel} · holders {connection.holderCount}
                </StatChip>
                <StatChip level={statLevel(wireActiveSubscriptions, 40, 80)}>
                    wire active {wireActiveSubscriptions} / cache {wireCacheEntries}
                </StatChip>
                <StatChip level={statLevel(maps.event.active, 15, 40)}>
                    {formatMapStat('event', maps.event, true)}
                </StatChip>
                <StatChip level={statLevel(maps.market.active, 30, 60)}>
                    {formatMapStat('market', maps.market, true)}
                </StatChip>
                <StatChip level={statLevel(maps.eventMarkets.active, 5, 15)}>
                    {formatMapStat('evtMkt', maps.eventMarkets, true)}
                </StatChip>
                <StatChip level={statLevel(maps.eventMarketsAll.active, 5, 15)}>
                    {formatMapStat('evtMktAll', maps.eventMarketsAll, true)}
                </StatChip>
                <StatChip level={statLevel(maps.eventQuery.active, 3, 8)}>
                    {formatMapStat('query', maps.eventQuery, true)}
                </StatChip>
                <StatChip>{formatMapStat('selection', maps.selection, false)}</StatChip>
                <StatChip>{formatMapStat('sport', maps.sport, true)}</StatChip>
                <StatChip>{formatMapStat('comp', maps.competition, true)}</StatChip>
                <StatChip>{formatMapStat('rab', maps.rabMarkets, true)}</StatChip>
                <BarActions>
                    <InfoButton
                        type='button'
                        aria-label='Legend — what the numbers mean'
                        aria-expanded={legendOpen}
                        title='Legend'
                        onClick={(): void => {
                            setLegendOpen((open) => !open);
                        }}
                    >
                        i
                    </InfoButton>
                    <BarButton
                        type='button'
                        onClick={(): void => {
                            barState.hide();
                        }}
                    >
                        hide
                    </BarButton>
                </BarActions>
            </BarRoot>
            <SocketStatsBarLegend
                isOpen={legendOpen}
                onClose={(): void => {
                    setLegendOpen(false);
                }}
            />
        </>
    );
});

export const SocketStatsBar = observer('SocketStatsBar', () => {
    const common = useCommon();
    const barState = SocketStatsBarState.get(common);

    if (!barState.shouldRender) {
        return null;
    }

    return <SocketStatsBarInner />;
});
